import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchTermsStatus, acceptTerms } from '@/lib/api';
import { useTermsPromptStore } from '@/store/termsPrompt';
import { TermsAcceptCard } from './TermsAcceptCard';
import styles from './TermsGate.module.css';

/**
 * On-demand terms accept card, raised by requestTermsAcceptance(docType) when
 * an earning-enable call returns 409 PAYOUT_TERMS_REQUIRED (a brand-new earner
 * the proactive TermsGate hasn't flagged yet). Fetches the current doc, shows
 * the accept card, and settles the awaiting promise so the api wrapper can
 * retry the enable. Dismissing settles false and the caller aborts.
 *
 * Mounted once in AppShell alongside TermsGate.
 */
export function TermsPromptHost() {
  const pending = useTermsPromptStore((s) => s.pending);
  const settle = useTermsPromptStore((s) => s.settle);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['terms', 'status', 'prompt', pending],
    queryFn: () => fetchTermsStatus([pending!]),
    enabled: Boolean(pending),
    staleTime: 0,
  });

  const accept = useMutation({
    mutationFn: () => acceptTerms(pending!),
    onSuccess: () => {
      // Keep the blocking gate + any status reads consistent.
      qc.invalidateQueries({ queryKey: ['terms', 'status'] });
      settle(true);
    },
  });

  if (!pending) return null;
  if (typeof document === 'undefined') return null;

  const doc = q.data?.[0];
  if (!doc) return null;

  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-terms-title">
      <TermsAcceptCard
        // Keyed per document for the same reason as TermsGate: a fresh card
        // (top of the document, unticked, scroll requirement re-armed) every
        // time the prompted document changes.
        key={`${doc.doc_type}:${doc.version}`}
        doc={doc}
        eyebrow="One more step to start earning"
        dismissLabel="Not now"
        isPending={accept.isPending}
        isError={accept.isError}
        onAccept={() => accept.mutate()}
        onDismiss={() => settle(false)}
      />
    </div>,
    document.body,
  );
}
